import axios from 'axios';
import keycloak from './keycloak';

export const getBaseApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
    return `${window.location.origin}/api/v1`;
  }
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000/api/v1';
};

export const api = axios.create({
  baseURL: getBaseApiUrl(),
});

const isPublicAuthRoute = (url?: string) => {
  if (!url) return false;
  return [
    '/auth/login',
    '/auth/refresh',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/resend-verification',
    '/auth/check-email',
    '/auth/whatsapp/request',
    '/auth/whatsapp/verify',
  ].some((route) => url.includes(route));
};

const redirectToLogin = () => {
  if (typeof window === 'undefined' || window.location.pathname === '/login') return;
  const destination = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/login?redirect=${encodeURIComponent(destination)}`;
};

api.interceptors.request.use(
  async (config) => {
    let token: string | null = null;

    if (typeof window !== 'undefined') {
      token = localStorage.getItem('scalesutra_token');
    }

    if (!token && keycloak && keycloak.token) {
      try {
        await keycloak.updateToken(30);
      } catch (err) {
        console.warn('Token update failed', err);
      }
      token = keycloak.token;
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (isPublicAuthRoute(originalRequest?.url)) {
      return Promise.reject(error);
    }

    // Check if error is 401 and request has not been retried yet
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (typeof window !== 'undefined') {
        const refreshToken = localStorage.getItem('scalesutra_refresh_token');

        if (refreshToken) {
          try {
            const refreshUrl = `${getBaseApiUrl()}/auth/refresh`;
            const res = await axios.post(refreshUrl, {
              refresh_token: refreshToken,
            });

            if (res.data?.data?.access_token) {
              const newAccessToken = res.data.data.access_token;
              const newRefreshToken = res.data.data.refresh_token || res.data.data.refreshToken || refreshToken;

              localStorage.setItem('scalesutra_token', newAccessToken);
              localStorage.setItem('scalesutra_refresh_token', newRefreshToken);

              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

              // Retry original request with updated token header
              return api(originalRequest);
            }
          } catch (refreshError) {
            console.error('[API Interceptor] Token refresh failed, clearing session:', refreshError);
            localStorage.removeItem('scalesutra_token');
            localStorage.removeItem('scalesutra_refresh_token');
            localStorage.removeItem('scalesutra_user');
            redirectToLogin();
          }
        } else {
          localStorage.removeItem('scalesutra_token');
          localStorage.removeItem('scalesutra_user');
          redirectToLogin();
        }
      }
    }

    return Promise.reject(error);
  }
);
